import * as React from 'react';
import {
  Input as ChakraInput,
  Field,
  Box,
  Stack,
  InputProps as ChakraInputProps,
} from '@chakra-ui/react';

export interface InputProps extends Omit<ChakraInputProps, 'size'> {
  label?: string;
  error?: string;
  success?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  startElement?: React.ReactNode;
  endElement?: React.ReactNode;
  containerClassName?: string;
  /** Size of the input - 'sm' | 'md' | 'lg' */
  inputSize?: 'sm' | 'md' | 'lg';
  /** Alias for inputSize - maintains compatibility */
  size?: 'sm' | 'md' | 'lg';
  required?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      success,
      hint,
      leftIcon,
      rightIcon,
      startElement,
      endElement,
      containerClassName,
      inputSize,
      size,
      required,
      disabled,
      id,
      ...props
    },
    ref
  ) => {
    // Support both size and inputSize (size is alias for backward compatibility)
    const resolvedSize = inputSize ?? size ?? 'md';
    // Generate ID for accessibility - always call React.useId() to comply with Rules of Hooks
    const generatedId = React.useId();
    // Only use ID when label exists (for htmlFor) to avoid hydration mismatch
    const inputId = id || (label ? generatedId : undefined);

    // Determine field status
    const invalid = !!error;
    const isSuccess = !!success && !error;

    // Filter out props that shouldn't go to DOM
    const { invalid: _invalid, ...filteredProps } = props as Record<string, unknown>;
    // _invalid is used for prop filtering only

    // Map sizes
    const sizeMap: Record<string, string> = {
      sm: 'sm',
      md: 'md',
      lg: 'lg',
    };

    // Native browser pickers (date/time/datetime-local) get suppressed by
    // Chakra's input reset (`appearance: none`) on WebKit/Blink, which
    // hides the calendar/clock icon and traps users into keyboard-only
    // entry. Restore the native trigger for these types only.
    const nativePickerTypes = new Set(['date', 'datetime-local', 'time', 'month', 'week']);
    const inputType = (props as { type?: string }).type;
    const usesNativePicker = inputType !== undefined && nativePickerTypes.has(inputType);

    // RULE 15: Conditional Field.Root wrapper - only use when label/error/hint/success exist
    // This fixes modal form styling issues where Field.Root adds unnecessary wrapper
    const hasFieldWrapper = !!(label || error || success || hint);

    const inputElement = (
      <Box position="relative" width={hasFieldWrapper ? undefined : '100%'}>
        {/* Left Icon / Start Element */}
        {(leftIcon || startElement) && (
          <Stack
            position="absolute"
            left="0"
            top="50%"
            transform="translateY(-50%)"
            paddingLeft={3}
            zIndex={1}
            color="fg.muted"
          >
            {leftIcon || startElement}
          </Stack>
        )}

        {/* Input */}
        <ChakraInput
          {...(inputId ? { id: inputId } : {})}
          ref={ref}
          size={sizeMap[resolvedSize] as 'sm' | 'md' | 'lg'}
          paddingLeft={leftIcon || startElement ? '40px' : undefined}
          paddingRight={rightIcon || endElement ? '40px' : undefined}
          borderColor={isSuccess ? 'border.success' : undefined}
          disabled={disabled}
          {...filteredProps}
          style={
            usesNativePicker
              ? // CSS allows `appearance: auto` for native pickers but
                // csstype's `WebkitAppearance` enum predates the 'auto'
                // value, so widen via `as React.CSSProperties` to keep
                // dts build clean while preserving runtime behavior.
                ({
                  WebkitAppearance: 'auto',
                  appearance: 'auto',
                  ...(filteredProps as { style?: React.CSSProperties }).style,
                } as React.CSSProperties)
              : (filteredProps as { style?: React.CSSProperties }).style
          }
        />

        {/* Right Icon / End Element */}
        {(rightIcon || endElement) && (
          <Stack
            position="absolute"
            right="0"
            top="50%"
            transform="translateY(-50%)"
            paddingRight={3}
            zIndex={1}
            color="fg.muted"
          >
            {rightIcon}
          </Stack>
        )}
      </Box>
    );

    // If no field wrapper needed, return input directly
    if (!hasFieldWrapper) {
      return containerClassName ? (
        <Box className={containerClassName}>{inputElement}</Box>
      ) : (
        inputElement
      );
    }

    // Otherwise, use full Field.Root wrapper with label/error/hint
    return (
      <Field.Root
        {...(invalid ? { invalid: true } : {})}
        className={containerClassName}
        disabled={disabled}
      >
        {/* Label - RULE 15: Revert htmlFor change, use implicit label wrapping instead */}
        {label && (
          <Box
            fontSize="sm"
            fontWeight="medium"
            color={invalid ? 'fg.error' : isSuccess ? 'fg.success' : 'fg.default'}
            display="block"
            marginBottom="4px"
          >
            {label}
            {required && (
              <Box as="span" color="fg.error" marginLeft={1}>
                *
              </Box>
            )}
          </Box>
        )}

        {/* Input Container */}
        {inputElement}

        {/* Error Message */}
        {error && (
          <Box fontSize="xs" color="fg.error" marginTop="4px">
            {error}
          </Box>
        )}

        {/* Success Message */}
        {success && !error && (
          <Box fontSize="xs" color="fg.success" marginTop="4px">
            {success}
          </Box>
        )}

        {/* Hint Message */}
        {hint && !error && !success && (
          <Box fontSize="xs" color="fg.muted" marginTop="4px">
            {hint}
          </Box>
        )}
      </Field.Root>
    );
  }
);

Input.displayName = 'Input';

// InputGroup components for composition
export interface InputGroupProps {
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

export const InputGroup = React.forwardRef<HTMLDivElement, InputGroupProps>(
  ({ children, size: _size = 'md', ...props }, ref) => {
    return (
      <Box ref={ref} position="relative" width="100%" {...props}>
        {children}
      </Box>
    );
  }
);

InputGroup.displayName = 'InputGroup';

export interface InputElementProps {
  children: React.ReactNode;
  pointerEvents?: 'none' | 'auto';
}

export const InputLeftElement = React.forwardRef<HTMLDivElement, InputElementProps>(
  ({ children, pointerEvents = 'none', ...props }, ref) => {
    return (
      <Box
        ref={ref}
        position="absolute"
        left="0"
        top="50%"
        transform="translateY(-50%)"
        paddingLeft="12px"
        zIndex="1"
        pointerEvents={pointerEvents}
        display="flex"
        alignItems="center"
        height="100%"
        {...props}
      >
        {children}
      </Box>
    );
  }
);

InputLeftElement.displayName = 'InputLeftElement';

export const InputRightElement = React.forwardRef<HTMLDivElement, InputElementProps>(
  ({ children, pointerEvents = 'none', ...props }, ref) => {
    return (
      <Box
        ref={ref}
        position="absolute"
        right="0"
        top="50%"
        transform="translateY(-50%)"
        paddingRight="12px"
        zIndex="1"
        pointerEvents={pointerEvents}
        display="flex"
        alignItems="center"
        height="100%"
        {...props}
      >
        {children}
      </Box>
    );
  }
);

InputRightElement.displayName = 'InputRightElement';

// Attach sub-components
(InputGroup as unknown as Record<string, unknown>).LeftElement = InputLeftElement;
(InputGroup as unknown as Record<string, unknown>).RightElement = InputRightElement;

export { Input };
